import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, map, shareReplay, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import { SnapshotService } from '../../Services/snapshot.service';
import { CustomsnapshotService } from '../../Services/customsnapshot.service';
import { SnapshotSharedVariablesService } from '../../Services/snapshot-shared-variables.service';
import { AlertMessageService } from 'src/app/common/services/alert-message.service';
import { CONFIG_TYPE, CrossTabConfiguration, EntryPointConfig, TopicsMode, TopicsRow } from '../topics-configuration.model';

@Component({
  selector: 'app-topics-grid',
  templateUrl: './topics-grid.component.html',
  styleUrls: ['./topics-grid.component.css']
})
export class TopicsGridComponent implements OnInit, OnDestroy {
  @Input() mode: TopicsMode = 'default';
  @Input() savedRows: any[] = [];
  @Input() disabled = false;
  @Input() snapshotTypeOptions: { label: string; value: string }[] = [];
  @Input() allSnapshotTypeOptions: { label: string; value: string }[] = [];
  @Input() mandatoryVariableMapping: any = [];
  @Input() dimensionList: any;

  isLoading = false;
  private destroy$ = new Subject<void>();
  private timers: ReturnType<typeof setTimeout>[] = [];

  // Loader is driven by a pending-request counter so overlapping calls never leave it stuck.
  private pendingRequests = 0;
  // Row-independent responses are fetched once and shared across all rows to avoid flooding the API.
  private widgetLibrary$?: Observable<any>;
  private entryPointData$?: Observable<any[]>;
  private leftPanel$ = new Map<string, Observable<any[]>>();

  constructor(
    private _configService: SnapshotService,
    private _customConfigService: CustomsnapshotService,
    private clientDetails: SnapshotSharedVariablesService,
    private _alertService: AlertMessageService
  ) {}

  get isBips(): boolean {
    return this.mode === 'bips';
  }

  private get modeLabel(): string {
    return this.isBips ? 'BIPS Calculation' : 'Default Brand';
  }

  ngOnInit(): void {
    this.restoreSavedRows();
    this.loadInitialBrands();
  }

  ngOnDestroy(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(setTimeout(fn, ms));
  }

  private startLoading(): void {
    this.pendingRequests++;
    this.isLoading = true;
  }

  private stopLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.isLoading = this.pendingRequests > 0;
  }

  private getWidgetLibrary(): Observable<any> {
    if (!this.widgetLibrary$) {
      this.widgetLibrary$ = this._customConfigService
        .getCustomSnapshotWidgetLibrary({ ClientName: this.clientDetails.clientDetails.client_name, SnapshotName: this.clientDetails.clientDetails.snapshotselected })
        .pipe(map((c: any) => (c?.Data ? JSON.parse(c.Data) : null)), shareReplay(1));
    }
    return this.widgetLibrary$;
  }

  private getEntryPointData(): Observable<any[]> {
    if (!this.entryPointData$) {
      const studyName = this.clientDetails.clientDetails.selectedStudy;
      this.entryPointData$ = this._configService
        .getSnapshotEntryPointData({ snapshot_name: this.clientDetails.clientDetails.snapshotselected, client_name: this.clientDetails.clientDetails.client_name, client_code: this.clientDetails.clientDetails.client_name })
        .pipe(
          map((c: any) => (c?.Data ? (JSON.parse(c.Data).EntryPoints || []).filter((item: any) => item.study === studyName) : [])),
          shareReplay(1)
        );
    }
    return this.entryPointData$;
  }

  private getLeftPanelData(payload: any[]): Observable<any[]> {
    const key = JSON.stringify(payload);
    let obs = this.leftPanel$.get(key);
    if (!obs) {
      obs = this._configService.getSnapshotLeftPanelData(payload)
        .pipe(map((data: any) => (data?.Data ? JSON.parse(data.Data) : [])), shareReplay(1));
      this.leftPanel$.set(key, obs);
    }
    return obs;
  }

  // ── Public API used by the parent ──────────────────────────────────
  hasConfiguredSection(): boolean {
    return this.rows.some(row => row.sectionName && row.sectionName.trim() !== '');
  }

  getSelectedRowIds(): string[] {
    return this.rows.filter(r => r.selected).map(r => r.rowId);
  }

  removeRows(rowIds: string[]): void {
    this.rows = this.rows.filter(r => !rowIds.includes(r.rowId));
  }

  private restoreSavedRows(): void {
    const sourceRows = this.savedRows || [];
    if (!sourceRows.length) {
      this.loadSectionOptionsForRow(0);
      return;
    }

    this.rows = sourceRows.map((r: any): TopicsRow => ({
      rowId:          r.rowId,
      selected:       false,
      snapshotType:   this.resolveUploadedSnapshotType(r.snapshotType),
      sectionName:    Array.isArray(r.sectionName) ? (r.sectionName[0] ?? '') : (r.sectionName ?? ''),
      subSectionName: Array.isArray(r.subSectionName) ? r.subSectionName : (r.subSectionName ? [r.subSectionName] : []),
      brandList:      r.brandList || [],
      dimension1:     this.isBips ? '' : (r.dimension1 || ''),
      dimension2:     this.isBips ? '' : (r.dimension2 || ''),
      dimension3:     this.isBips ? '' : (r.dimension3 || ''),
      // Name is the MetricOption the dropdowns are keyed on; old saved rows stored it in responseCode.
      entryPoint1: r.EntryPoint1?.Level2?.[0]?.MetricOptions?.map(
        (m: any) => m.Name ?? m.responseCode) || [],
      entryPoint2: r.EntryPoint2?.Level2?.[0]?.MetricOptions?.map(
        (m: any) => m.Name ?? m.responseCode) || [],
      entryPoint3: r.EntryPoint3?.Level2?.[0]?.MetricOptions?.map(
        (m: any) => m.Name ?? m.responseCode) || []
    }));

    this.rows.forEach((row, i) => {

      const restoredLevel1Values = [
        sourceRows[i].EntryPoint1?.Level1 || null,
        sourceRows[i].EntryPoint2?.Level1 || null,
        sourceRows[i].EntryPoint3?.Level1 || null
      ];
      this.rowLevel1Values[i] = restoredLevel1Values;

      const snapshotLabel = this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType;

      const brandCallback = (row.entryPoint1.length || row.entryPoint2.length || row.entryPoint3.length)
        ? () => this.restoreBrandOptions(i, restoredLevel1Values)
        : undefined;

      this.onSnapshotTypeChange(snapshotLabel, i, true);

      this.later(() => {
        this.onSectionChange(row.sectionName, i, true, brandCallback);

        const subSections = row.subSectionName.length ? row.subSectionName : [''];
        subSections.forEach((subSection, idx) => {
          this.later(() => {
            this.onSubSectionChange(subSection, i, true, idx === subSections.length - 1 ? brandCallback : undefined);
          }, (idx + 1) * 1000);
        });
      }, 1000);
    });
  }

  multiSelectOptions = [
    { label: 'Multiple', value: 'multiple' },
    { label: 'Single', value: 'single' },
  ];

  rows: TopicsRow[] = [
    {
      rowId: crypto.randomUUID(),
      selected: true,
      snapshotType: 'multiBrand',
      sectionName: '',
      subSectionName: [],
      entryPoint1: [],
      entryPoint2: [],
      entryPoint3: [],
      dimension1: '',
      dimension2: '',
      dimension3: '',
      brandList: [],
    }
  ];

  openDropdownIndex = -1;
  epOpenState: { [key: string]: boolean } = {};
  dropdownSearch: { [key: string]: string } = {};

  toggleEpDropdown(i: number, field: string): void {
    const key = `${i}_${field}`;
    const willOpen = !this.epOpenState[key];
    this.epOpenState[key] = willOpen;
    if (willOpen) { this.dropdownSearch[key] = ''; }
  }

  closeEpDropdown(i: number, field: string): void {
    const key = `${i}_${field}`;
    this.epOpenState[key] = false;
  }

  isEpOpen(i: number, field: string): boolean {
    return !!this.epOpenState[`${i}_${field}`];
  }

  filterOptions(items: any[], search: string): any[] {
    if (!search?.trim()) return items;
    const term = search.trim().toLowerCase();
    return items.filter(item => {
      const str = typeof item === 'string' ? item : (item.label ?? '');
      return str.toLowerCase().includes(term);
    });
  }

  getEpLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getBrandLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getSelectedValuesLabel(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    if (!values?.length) return 'Select';
    if (this.areAllOptionsSelected(values, options)) return 'All';
    return values.length > 1 ? 'Multiple' : this.getOptionLabel(values[0], options);
  }

  getSelectedValuesTooltip(values: string[], options: Array<string | { label: string; value: string }> = []): string {
    if (!values?.length) return 'Select';
    if (this.areAllOptionsSelected(values, options)) return 'All';
    return values.map(value => this.getOptionLabel(value, options)).join(', ');
  }

  private areAllOptionsSelected(values: string[], options: Array<string | { label: string; value: string }>): boolean {
    const optionValues = options.map(option => typeof option === 'string' ? option : option.value);
    return optionValues.length > 0 && optionValues.every(value => values.includes(value));
  }

  private getOptionLabel(value: string, options: Array<string | { label: string; value: string }>): string {
    const option = options.find(item => (typeof item === 'string' ? item : item.value) === value);
    return typeof option === 'string' ? option : option?.label ?? value;
  }

  getSectionLabel(value: string): string {
    return value || 'Select';
  }

  toggleEpOption(values: string[], value: string): void {
    const idx = values.indexOf(value);
    if (idx >= 0) values.splice(idx, 1); else values.push(value);
  }

  isEpChecked(values: string[], value: string): boolean {
    return values?.some(v => (v ?? '').toString().trim().toLowerCase() === (value ?? '').toString().trim().toLowerCase());
  }

  private normalizeExcelKey(value: string): string {
    return (value ?? '').toString().replace(/[\s_\-()]/g, '').toLowerCase();
  }

  private getExcelCell(row: any, ...names: string[]): any {
    if (!row) {
      return '';
    }

    const normalizedRow = new Map<string, any>();
    Object.keys(row).forEach(key => {
      normalizedRow.set(this.normalizeExcelKey(key), row[key]);
    });

    for (const name of names) {
      const value = normalizedRow.get(this.normalizeExcelKey(name));
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    for (const [key, value] of normalizedRow.entries()) {
      if (key.includes('brand') && value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return '';
  }

  private splitExcelList(value: any): string[] {
    if (!value) {
      return [];
    }
    return value.toString()
      .split(/[,;|]/)
      .map((x: string) => x.trim())
      .filter((x: string) => x);
  }

  private resolveUploadedSnapshotType(rawType: string): string {
    const value = (rawType || '').trim();
    const match = this.findSnapshotTypeOption(value);
    if (match) {
      return match.value;
    }
    if (this.isMultiBrandSnapshotType(value)) {
      return this.getMultiBrandOptionValue();
    }
    return value;
  }

  private findSnapshotTypeOption(snapshotType: string): { label: string; value: string } | undefined {
    const value = (snapshotType || '').trim();
    return [...this.allSnapshotTypeOptions, ...this.snapshotTypeOptions].find(o =>
      o.value.toLowerCase() === value.toLowerCase() ||
      o.label.toLowerCase() === value.toLowerCase() ||
      this.normalizeExcelKey(o.label) === this.normalizeExcelKey(value) ||
      this.normalizeExcelKey(o.value) === this.normalizeExcelKey(value)
    );
  }

  private getMultiBrandOptionValue(): string {
    return this.snapshotTypeOptions.find(o => this.isMultiBrandSnapshotType(o.value) || this.isMultiBrandSnapshotType(o.label))?.value
      ?? 'multiBrand';
  }

  private getDisallowedDefaultBrandLabel(snapshotType: string): string | null {
    if (!snapshotType || this.isMultiBrandSnapshotType(snapshotType)) {
      return null;
    }

    const normalized = this.normalizeExcelKey(snapshotType);
    if (normalized === 'singlebrand') return 'Single Brand';
    if (normalized === 'multimarket' || normalized === 'multiplemarket' || normalized === 'multiplemarkets') return 'Multi Market';
    if (normalized === 'audiencesnapshot' || normalized === 'audience') return 'Audience Snapshot';

    return this.findSnapshotTypeOption(snapshotType)?.label ?? snapshotType;
  }

  isMultiBrandSnapshotType(snapshotType: string): boolean {
    const normalized = this.normalizeExcelKey(snapshotType);
    return normalized === 'multibrand'
      || normalized === 'multiplebrand'
      || normalized === 'multiplebrands';
  }

  private parseUploadedBrandList(row: any, snapshotType: string): string[] {
    if (!this.isMultiBrandSnapshotType(snapshotType)) {
      return [];
    }

    const raw = this.getExcelCell(row, 'Brand List', 'BrandList', 'brandList', 'Selected Brand', 'Selected Brands');
    return this.splitExcelList(raw);
  }

  private syncUploadedBrandSelection(i: number): void {
    const row = this.rows[i];
    if (!row || !this.isMultiBrandSnapshotType(row.snapshotType) || !row.brandList?.length) {
      return;
    }

    const options = this.rowBrandOptions[i] || [];
    if (!options.length) {
      return;
    }

    row.brandList = row.brandList
      .map(uploaded => {
        const match = options.find(o =>
          this.normalizeExcelKey(o.value) === this.normalizeExcelKey(uploaded) ||
          this.normalizeExcelKey(o.label) === this.normalizeExcelKey(uploaded)
        );
        return match?.value ?? uploaded;
      })
      .filter((value, index, list) => list.indexOf(value) === index);
  }

  private restoreUploadedRowOptions(): void {
    this.rows.forEach((row, i) => {
      if (!this.isMultiBrandSnapshotType(row.snapshotType)) {
        row.brandList = [];
      }
      const snapshotLabel = this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType;
      const uploadedBrands = this.isMultiBrandSnapshotType(row.snapshotType) ? [...(row.brandList || [])] : [];
      const brandCallback = () => {
        row.brandList = uploadedBrands;
        this.getBrandList(i);
      };
      this.onSnapshotTypeChange(snapshotLabel, i, true);
      this.later(() => {
        this.onSectionChange(row.sectionName, i, true, brandCallback);
      }, 1000);
    });
  }

  isSingleBrandSelect(row: TopicsRow): boolean {
    const type = row.snapshotType?.toLowerCase() ?? '';
    const label = (this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType)?.toLowerCase() ?? '';
    return type === 'singlebrand' || label.includes('single brand') ||
           type === 'multimarket' || type === 'multiplemarkets' || label.includes('multiple markets');
  }

  isAllEpSelected(values: string[], options: { label: string; value: string }[]): boolean {
    return options.length > 0 && options.every(o => values.includes(o.value));
  }

  toggleSelectAllEp(values: string[], options: { label: string; value: string }[], i: number): void {
    if (this.isAllEpSelected(values, options)) {
      values.splice(0, values.length);
    } else {
      options.forEach(o => { if (!values.includes(o.value)) values.push(o.value); });
    }
    this.getBrandList(i);
  }

  isBrandListDisabled(row: TopicsRow, i: number): boolean {
    if (!this.isMultiBrandSnapshotType(row.snapshotType)) return true;
    if (!row.sectionName) return true;
    const ep1Enabled = (this.rowEntryPoint1Options[i]?.length ?? 0) > 0;
    const ep2Enabled = !!this.rowEntryPoint2Enabled[i];
    const ep3Enabled = !!this.rowEntryPoint3Enabled[i];
    if (ep1Enabled && !row.entryPoint1?.length) return true;
    if (ep2Enabled && !row.entryPoint2?.length) return true;
    if (ep3Enabled && !row.entryPoint3?.length) return true;
    if (!(this.rowBrandOptions[i]?.length)) return true;
    return false;
  }

  onBrandChange(row: TopicsRow, value: string): void {
    if (!this.isMultiBrandSnapshotType(row.snapshotType)) {
      return;
    }
    this.toggleEpOption(row.brandList, value);
  }

  onBrandRadioChange(row: TopicsRow, value: string): void {
    if (!this.isMultiBrandSnapshotType(row.snapshotType)) {
      row.brandList = [];
      return;
    }
    row.brandList = [value];
  }

  getBrandRankLabel(row: TopicsRow, value: string): string {
    const index = row.brandList.indexOf(value);
    return index >= 0 ? `${index + 1}` : '';
  }

  onEpChange(values: string[], value: string, i: number): void {
    this.toggleEpOption(values, value);
    this.getBrandList(i);
  }

  rowSectionOptions: { [i: number]: string[] } = {};
  rowSubSectionMap: { [i: number]: { [section: string]: string[] } } = {};
  rowEntryPoint1Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint2Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint3Options: { [i: number]: { label: string; value: string }[] } = {};
  rowEntryPoint2Enabled: { [i: number]: string } = {};
  rowEntryPoint3Enabled: { [i: number]: string } = {};
  rowLevel1Values: { [i: number]: (string | null)[] } = {};
  rowEntryPointData: { [i: number]: any[] } = {};
  rowBrandData: { [i: number]: any[] } = {};
  rowBrandOptions: { [i: number]: { label: string; value: string }[] } = {};
  crossTabConfig: CrossTabConfiguration[] = [];

  toggleDropdown(i: number): void {
    const willOpen = this.openDropdownIndex !== i;
    this.openDropdownIndex = willOpen ? i : -1;
    if (willOpen) { this.dropdownSearch[`${i}_snapshotType`] = ''; }
  }

  closeDropdown(i: number): void {
    if (this.openDropdownIndex === i) this.openDropdownIndex = -1;
  }

  // Clearing the section also clears the fields that become disabled without it.
  resetSection(row: TopicsRow, i: number): void {
    row.sectionName = '';
    row.subSectionName = [];
    row.entryPoint1 = [];
    row.entryPoint2 = [];
    row.entryPoint3 = [];
    row.brandList = [];
    this.dropdownSearch[`${i}_section`] = '';
  }

  resetSubSection(row: TopicsRow, i: number): void {
    row.subSectionName = [];
    this.dropdownSearch[`${i}_subSection`] = '';
  }

  resetEntryPoint(values: string[], field: 'ep1' | 'ep2' | 'ep3', i: number): void {
    values.splice(0, values.length);
    this.dropdownSearch[`${i}_${field}`] = '';
    this.getBrandList(i);
  }

  resetBrands(row: TopicsRow, i: number): void {
    row.brandList = [];
    this.dropdownSearch[`${i}_brand`] = '';
  }

  loadInitialBrands(): void {
    const payload = [{
      clientName: this.clientDetails.clientDetails.client_name,
      studyName: this.clientDetails.clientDetails.selectedStudy,
      isMandatoryLoadClicked: false
    }];

    this._configService
      .getSnapshotLeftPanelData(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (!res?.Data) {
            return;
          }

          const parsedData = JSON.parse(res.Data);

          const brands = parsedData.filter(
            (x: any) => x.Level1 === 'Brands'
          );

          const options = (Array.from(
            new Set(
              brands.map((x: any) => x.MetricOption)
            )
          ) as string[]).map(x => ({
            label: x,
            value: x
          }));

          this.rows.forEach((_, index) => {
            this.rowBrandOptions[index] = [...options];
          });
        }
      });
  }

  getBrandList(i: number): void {
    this.updateEntryPointOptions(i);
    const row = this.rows[i];
    const level1Values = this.rowLevel1Values[i] ?? [];
    const options = this.rowEntryPoint1Options[i] ?? [];

    const buildEntryPoint = (selectedValues: string[], level1: string | null): EntryPointConfig => {
      const epData: any[] = this.rowEntryPointData[i] ?? [];
      const matchingItem = epData.find((item: any) => item.Level1 === level1);
      const varCode: string | null = matchingItem?.VariableCode ?? null;
      return {
        Level1: level1,
        Level2: [{
          Parent: level1 ?? '',
          MetricOptions: selectedValues.map(val => {
            const opt = options.find(o => o.value === val);
            const matchingItemInner = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == val);
            return { Name: opt?.label ?? val, responseCode: matchingItemInner?.ResponseCode ?? "", isActive: true };
          })
        }],
        variableCode: varCode
      };
    };

    const config: CrossTabConfiguration = {
      EntryPoint1: row.entryPoint1?.length ? buildEntryPoint(row.entryPoint1, level1Values[0] ?? null) : null,
      EntryPoint2: row.entryPoint2?.length ? buildEntryPoint(row.entryPoint2, level1Values[1] ?? null) : null,
      EntryPoint3: row.entryPoint3?.length ? buildEntryPoint(row.entryPoint3, level1Values[2] ?? null) : null,
      clientName: this.clientDetails.clientDetails.client_name,
      mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
      dimension: this.isBips ? null : (this.dimensionList ?? null),
      studyName: this.clientDetails.clientDetails.selectedStudy,
      isMandatoryLoadClicked: false,
      snapshotType: row.snapshotType
    };
    const existing = this.crossTabConfig.findIndex((_, idx) => idx === i);
    if (existing >= 0) {
      this.crossTabConfig[existing] = config;
    } else {
      this.crossTabConfig.push(config);
    }

    const requstObject = this.convertToSeparated(config);
    this.startLoading();
    this.getLeftPanelData(requstObject)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (parsedData: any[]) => {
          this.stopLoading();
          const brandData = parsedData.filter((item: any) => item.Level1 === 'Brands');
          this.rowBrandData[i] = brandData;
          const brandMetricOptions: string[] = Array.from(
            new Set(brandData.map((item: any) => item.MetricOption).filter((v: any) => v != null))
          );
          this.rowBrandOptions[i] = brandMetricOptions.map((opt: string) => ({ label: opt, value: opt }));
          this.syncUploadedBrandSelection(i);
        },
        error: () => { this.stopLoading(); }
      });
  }

  private restoreBrandOptions(i: number, restoredLevel1Values: (string | null)[]): void {
    const row = this.rows[i];
    const epData: any[] = this.rowEntryPointData[i] ?? [];

    const buildEP = (selectedValues: string[], level1: string | null): EntryPointConfig | null => {
      if (!selectedValues?.length || !level1) return null;
      const matchingItem = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == selectedValues[0]);
      return {
        Level1: level1,
        Level2: [{
          Parent: level1,
          MetricOptions: selectedValues.map(val => {
            const matchingItemInner = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == val);
            return { Name: val, responseCode: matchingItemInner?.ResponseCode ?? "", isActive: true };
          })
        }],
        variableCode: matchingItem?.VariableCode ?? null
      };
    };

    const singleConfig: CrossTabConfiguration[] = [{
      EntryPoint1: buildEP(row.entryPoint1, restoredLevel1Values[0]),
      EntryPoint2: buildEP(row.entryPoint2, restoredLevel1Values[1]),
      EntryPoint3: buildEP(row.entryPoint3, restoredLevel1Values[2]),
      clientName: this.clientDetails.clientDetails.client_name,
      mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
      dimension: this.isBips ? null : (this.dimensionList ?? null),
      studyName: this.clientDetails.clientDetails.selectedStudy,
      isMandatoryLoadClicked: false,
      snapshotType: row.snapshotType
    }];

    this.getLeftPanelData(singleConfig)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (parsedData: any[]) => {
          const brandMetricOptions: string[] = Array.from(
            new Set(
              parsedData
                .filter((item: any) => item.Level1 === 'Brands')
                .map((item: any) => item.MetricOption)
                .filter((v: any) => v != null)
            )
          );
          this.rowBrandOptions[i] = brandMetricOptions.map((opt: string) => ({ label: opt, value: opt }));
          this.syncUploadedBrandSelection(i);
        },
        error: () => {}
      });
  }

  onSnapshotTypeChange(row: string, i: number, isRestore = false): void {
    this.openDropdownIndex = -1;
    if (!this.isMultiBrandSnapshotType(this.rows[i]?.snapshotType) && !this.isMultiBrandSnapshotType(row)) {
      this.rows[i].brandList = [];
    }
    if (!isRestore) {
      this.rows[i].sectionName = '';
      this.rows[i].subSectionName = [];
      this.rowSectionOptions[i] = [];
      this.rowSubSectionMap[i] = {};
    }
    this.clientDetails.topicsSelection.snapshotType = row;
    if (!isRestore) {
      this.clientDetails.topicsSelection.sectionName = '';
      this.clientDetails.topicsSelection.subSectionName = '';
    }
    this.startLoading();
    this.getWidgetLibrary()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (snapshotData: any) => {
        this.stopLoading();
        if (snapshotData) {
          this.applyWidgetLibrary(snapshotData, row, i, isRestore);
        }
      },
      error: () => { this.stopLoading(); }
    });
  }

  private applyWidgetLibrary(snapshotData: any, row: string, i: number, isRestore: boolean): void {
    const filteredTypes = snapshotData?.snapshotType?.filter((type: any) =>
      type.snapshotType_name === row
    ) ?? [];
    const sections: string[] = [];
    const subSectionMap: { [section: string]: string[] } = {};

    filteredTypes.forEach((type: any) => {
      type.section?.forEach((section: any) => {
        const sectionName: string = section.section_name;
        if (!sections.includes(sectionName)) sections.push(sectionName);
        if (!subSectionMap[sectionName]) subSectionMap[sectionName] = [];
        section.subsection?.forEach((sub: any) => {
          const subName: string = sub.subsection_name;
          if (subName && subName !== 'NA' && !subSectionMap[sectionName].includes(subName)) subSectionMap[sectionName].push(subName);
        });
      });
    });

    this.rowSectionOptions[i] = sections;
    this.rowSubSectionMap[i] = subSectionMap;

    // If current sectionName is not in the new options, reset it
    if (!isRestore && this.rows[i].sectionName && !sections.includes(this.rows[i].sectionName)) {
      this.rows[i].sectionName = '';
      this.rows[i].subSectionName = [];
    }
  }

  getLabelForValue(value: string): string {
    if (!value) return 'Select';
    return this.findSnapshotTypeOption(value)?.label
      ?? this.snapshotTypeOptions.find(o => o.value === value)?.label
      ?? value;
  }

  onSectionChange(value: string, i: number, isRestore = false, afterLoad?: () => void): void {
    if (!isRestore) {
      this.rows[i].sectionName = value;
      this.rows[i].subSectionName = [];
    }
    const hasSubSections = (this.rowSubSectionMap[i]?.[value] ?? []).length > 0;
    if (!hasSubSections) {
      this.onSectionSubsectionChange(i, afterLoad);
    }
  }

  isEntryPoint1Disabled(i: number, row: TopicsRow): boolean {
    return !row.sectionName || !(this.rowEntryPoint1Options[i]?.length);
  }

  onSubSectionChange(value: string, i: number, isRestore = false, afterLoad?: () => void): void {
    this.clientDetails.topicsSelection.subSectionName = value;
    this.onSectionSubsectionChange(i, afterLoad);
  }

  toggleSubSection(row: TopicsRow, value: string, i: number): void {
    const idx = row.subSectionName.indexOf(value);
    if (idx >= 0) {
      row.subSectionName.splice(idx, 1);
    } else {
      row.subSectionName.push(value);
    }
    this.onSubSectionChange(row.subSectionName[0] ?? '', i);
  }

  getSubSectionLabel(values: string[], options: string[] = []): string {
    return this.getSelectedValuesLabel(values, options);
  }

  getAvailableSubSections(row: TopicsRow, i: number): string[] {
    return this.rowSubSectionMap[i]?.[row.sectionName] ?? [];
  }

  get allSelected(): boolean {
    return this.rows.length > 0 && this.rows.every(r => r.selected);
  }

  private updateEntryPointOptions(i: number): void {
    const row = this.rows[i];
    const entryPointData = this.rowEntryPointData[i];
    if (!entryPointData) return;

    const levels = this.rowLevel1Values[i] ?? [];
    const selections = [row.entryPoint1, row.entryPoint2, row.entryPoint3];
    const selectedCodes: string[][] = [];
    const options = selections.map((values, level) => {
      const matchingItems = entryPointData.filter(item =>
        item.Level1 === levels[level] &&
        selectedCodes.every((codes, parentLevel) =>
          String(item[`e${parentLevel + 1}_variable_code`] ?? '')
            .split(',')
            .some(code => codes.includes(code.trim()))
        )
      );
      const metrics: string[] = Array.from(new Set(
        matchingItems.map(item => item.MetricOption).filter(value => value != null)
      ));
      values.splice(0, values.length, ...values.filter(value => metrics.includes(value)));
      selectedCodes.push(
        matchingItems.filter(item => values.includes(item.MetricOption))
          .map(item => String(item.ResponseCode))
      );
      return metrics.map(value => ({ label: value, value }));
    });

    [this.rowEntryPoint1Options[i], this.rowEntryPoint2Options[i], this.rowEntryPoint3Options[i]] = options;
  }

  onSectionSubsectionChange(i: number, afterLoad?: () => void): void {
    this.startLoading();
    this.getEntryPointData()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (entryPointData: any[]) => {
        this.stopLoading();
        this.applyEntryPointData(entryPointData, i, afterLoad);
      },
      error: () => { this.stopLoading(); }
    });
  }

  private applyEntryPointData(entryPointData: any[], i: number, afterLoad?: () => void): void {
    this.rowEntryPointData[i] = entryPointData;
    this.clientDetails.entryPointsDetails = entryPointData;

    const uniqueLevel1: string[] = Array.from(
      new Set((entryPointData as any[]).map((item: any) => item.Level1).filter((v: any) => v != null && v !== ''))
    );

    this.rowLevel1Values[i] = uniqueLevel1;
    this.rowEntryPoint2Enabled[i] = uniqueLevel1.length >= 2 ? uniqueLevel1[1] : '';
    this.rowEntryPoint3Enabled[i] = uniqueLevel1.length >= 3 ? uniqueLevel1[2] : '';

        this.updateEntryPointOptions(i);

    if (afterLoad) afterLoad();
  }

  selectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.rows.forEach(r => r.selected = checked);
  }

  addNewRow(): void {
    if (this.rows.length >= 200) {
      this._alertService.showAlert({ header: 'Warning', message: 'Maximum 200 rows allowed. Cannot add more rows.', show: true });
      return;
    }
    this.rows.push({
      rowId: crypto.randomUUID(),
      selected: false,
      snapshotType: this.getMultiBrandOptionValue(),
      sectionName: '',
      subSectionName: [],
      entryPoint1: [],
      entryPoint2: [],
      entryPoint3: [],
      dimension1: '',
      dimension2: '',
      dimension3: '',
      brandList: [],
    });
    const newIndex = this.rows.length - 1;
    this.rowBrandOptions[newIndex] = [...(this.rowBrandOptions[0] || [])];
    this.loadSectionOptionsForRow(newIndex);   // add this line
  }

  duplicateRow(index: number): void {
    if (this.rows.length >= 200) {
      this._alertService.showAlert({ header: 'Warning', message: 'Maximum 200 rows allowed. Cannot duplicate row.', show: true });
      return;
    }
    const original = this.rows[index];
    const copy: TopicsRow = {
      ...original,
      rowId: crypto.randomUUID(),
      entryPoint1: [...original.entryPoint1],
      entryPoint2: [...original.entryPoint2],
      entryPoint3: [...original.entryPoint3],
      brandList: this.isMultiBrandSnapshotType(original.snapshotType) ? [...original.brandList] : [],
    };
    this.rows.splice(index + 1, 0, copy);
    const newIndex = index + 1;

    // Shift all per-row maps for rows that moved down by one
    for (let i = this.rows.length - 1; i > newIndex; i--) {
      this.rowSectionOptions[i]      = this.rowSectionOptions[i - 1];
      this.rowSubSectionMap[i]       = this.rowSubSectionMap[i - 1];
      this.rowEntryPoint1Options[i]  = this.rowEntryPoint1Options[i - 1];
      this.rowEntryPoint2Options[i]  = this.rowEntryPoint2Options[i - 1];
      this.rowEntryPoint3Options[i]  = this.rowEntryPoint3Options[i - 1];
      this.rowEntryPoint2Enabled[i]  = this.rowEntryPoint2Enabled[i - 1];
      this.rowEntryPoint3Enabled[i]  = this.rowEntryPoint3Enabled[i - 1];
      this.rowLevel1Values[i]        = this.rowLevel1Values[i - 1];
      this.rowEntryPointData[i]      = this.rowEntryPointData[i - 1];
      this.rowBrandData[i]           = this.rowBrandData[i - 1];
      this.rowBrandOptions[i]        = this.rowBrandOptions[i - 1];
    }

    // Copy source row's maps to the new index
    this.rowSectionOptions[newIndex]     = this.rowSectionOptions[index] ? [...this.rowSectionOptions[index]] : [];
    this.rowSubSectionMap[newIndex]      = this.rowSubSectionMap[index] ? { ...this.rowSubSectionMap[index] } : {};
    this.rowEntryPoint1Options[newIndex] = this.rowEntryPoint1Options[index] ? [...this.rowEntryPoint1Options[index]] : [];
    this.rowEntryPoint2Options[newIndex] = this.rowEntryPoint2Options[index] ? [...this.rowEntryPoint2Options[index]] : [];
    this.rowEntryPoint3Options[newIndex] = this.rowEntryPoint3Options[index] ? [...this.rowEntryPoint3Options[index]] : [];
    this.rowEntryPoint2Enabled[newIndex] = this.rowEntryPoint2Enabled[index] ?? '';
    this.rowEntryPoint3Enabled[newIndex] = this.rowEntryPoint3Enabled[index] ?? '';
    this.rowLevel1Values[newIndex]       = this.rowLevel1Values[index] ? [...this.rowLevel1Values[index]] : [];
    this.rowEntryPointData[newIndex]     = this.rowEntryPointData[index] ? [...this.rowEntryPointData[index]] : [];
    this.rowBrandData[newIndex]          = this.rowBrandData[index] ? [...this.rowBrandData[index]] : [];
    this.rowBrandOptions[newIndex]       = this.rowBrandOptions[index] ? [...this.rowBrandOptions[index]] : [];
  }

  //A row created with a preset snapshot type has no section options until they are loaded here.
  private loadSectionOptionsForRow(i: number): void {
      const row = this.rows[i];
      if (!row) return;
      const label = this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType;
      this.onSnapshotTypeChange(label, i, true);
  }

  importExcel(file: File): void {
    this.startLoading();

    const fileReader = new FileReader();

    fileReader.onload = (e: any) => {

      try {

        const workbook = XLSX.read(e.target.result, {
          type: 'binary'
        });

        const sheetName = workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];

        const aoa: any[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          raw: false
        });

        const headers = (aoa[0] || []).map((h: any) => (h ?? '').toString());
        const findCol = (...names: string[]) => {
          const wanted = names.map(n => this.normalizeExcelKey(n));
          const exact = headers.findIndex(h => wanted.includes(this.normalizeExcelKey(h)));
          if (exact >= 0) {
            return exact;
          }
          return headers.findIndex(h => wanted.some(n => this.normalizeExcelKey(h).includes(n)));
        };

        let brandCol = findCol('Brand List', 'BrandList', 'brandList', 'Selected Brand', 'Selected Brands');
        if (brandCol < 0) {
          brandCol = headers.findIndex(h => this.normalizeExcelKey(h).includes('brand'));
        }
        if (brandCol < 0) {
          brandCol = this.isBips ? 6 : 9;
        }

        const snapshotCol = findCol('Snapshot Type', 'snapshotType');
        const sectionCol = findCol('Section Name', 'sectionName');
        const subSectionCol = findCol('Sub Section Name', 'subSectionName');
        const ep1Col = findCol('Entry Point 1', 'EntryPoint1');
        const ep2Col = findCol('Entry Point 2', 'EntryPoint2');
        const ep3Col = findCol('Entry Point 3', 'EntryPoint3');
        const dim1Col = this.isBips ? -1 : findCol('Dimension 1', 'dimension1');
        const dim2Col = this.isBips ? -1 : findCol('Dimension 2', 'dimension2');
        const dim3Col = this.isBips ? -1 : findCol('Dimension 3', 'dimension3');
        const cellAt = (row: any[], index: number) => index >= 0 && row[index] != null ? String(row[index]) : '';

        const parsedRows: TopicsRow[] = aoa.slice(1)
          .filter(r => (r || []).some((c: any) => String(c ?? '').trim()))
          .map((r: any[]): TopicsRow => {
            const snapshotType = this.resolveUploadedSnapshotType(cellAt(r, snapshotCol >= 0 ? snapshotCol : 0));
            const brandList = this.isMultiBrandSnapshotType(snapshotType)
              ? this.splitExcelList(cellAt(r, brandCol))
              : [];
            return {
              rowId: crypto.randomUUID(),
              selected: false,
              snapshotType,
              sectionName: cellAt(r, sectionCol >= 0 ? sectionCol : 1),
              subSectionName: this.splitExcelList(cellAt(r, subSectionCol)),
              entryPoint1: this.splitExcelList(cellAt(r, ep1Col)),
              entryPoint2: this.splitExcelList(cellAt(r, ep2Col)),
              entryPoint3: this.splitExcelList(cellAt(r, ep3Col)),
              dimension1: cellAt(r, dim1Col),
              dimension2: cellAt(r, dim2Col),
              dimension3: cellAt(r, dim3Col),
              brandList
            };
          });

        const skippedTypes = [...new Set(
          parsedRows
            .map(row => this.getDisallowedDefaultBrandLabel(row.snapshotType))
            .filter((label): label is string => !!label)
        )];
        const uploadedRows = parsedRows
          .filter(row => this.isMultiBrandSnapshotType(row.snapshotType))
          .map(row => ({
            ...row,
            snapshotType: this.getMultiBrandOptionValue()
          }));

        if (!uploadedRows.length) {
          this._alertService.showAlert({
            header: 'Warning',
            message: skippedTypes.length
              ? `${this.modeLabel} can only be uploaded for Multi Brand. ${skippedTypes.join(', ')} ${skippedTypes.length === 1 ? 'is' : 'are'} not allowed.`
              : `${this.modeLabel} can only be uploaded for Multi Brand. Single Brand, Multi Market, and Audience Snapshot are not allowed.`,
            show: true
          });
          this.stopLoading();
          return;
        }

        if (uploadedRows.length > 200) {
          this._alertService.showAlert({ header: 'Warning', message: 'Maximum 200 rows allowed. Cannot upload more rows.', show: true });
          this.stopLoading();
          return;
        }

        this.rows = uploadedRows;
        this.rows.forEach((row, i) => {
          if (!this.rowBrandOptions[i]?.length) {
            const existing = Object.values(this.rowBrandOptions).find(o => o?.length);
            if (existing) {
              this.rowBrandOptions[i] = [...existing];
            }
          }
          this.syncUploadedBrandSelection(i);
        });
        this.restoreUploadedRowOptions();

        this._alertService.showAlert({
          header: skippedTypes.length ? 'Warning' : 'Success',
          message: skippedTypes.length
            ? `Only Multi Brand rows were uploaded. ${this.modeLabel} cannot be uploaded for ${skippedTypes.join(', ')}. Click Submit to save changes.`
            : 'Template uploaded successfully. Click Submit to save changes.',
          show: true
        });

      } catch (error) {

        console.error(error);

        this._alertService.showAlert({
          header: 'Error',
          message: 'Invalid template uploaded.',
          show: true
        });
      }

      this.stopLoading();
    };

    fileReader.readAsBinaryString(file);
  }

  // Returns null when validation fails; the alert is already shown.
  getPayloadRows(): any[] | null {
    const buildEntryPoint = (
      i: number,
      selectedValues: string[],
      level1: string | null
    ): EntryPointConfig | null => {
      if (!selectedValues?.length) return null;
      const epData: any[] = this.rowEntryPointData[i] ?? [];
      const options = this.rowEntryPoint1Options[i] ?? [];
      const matchingItem = epData.find((item: any) => item.Level1 === level1);
      const varCode: string | null = matchingItem?.VariableCode ?? null;
      return {
        Level1: level1,
        Level2: [{
          Parent: level1 ?? '',
          MetricOptions: selectedValues.map(val => {
            const opt = options.find((o: any) => o.value === val);
            const matchingItemInner = epData.find((item: any) => item.Level1 === level1 && item.MetricOption == val);
            return { Name: opt?.label ?? val, responseCode: matchingItemInner?.ResponseCode ?? "", isActive: true };
          })
        }],
        variableCode: varCode
      };
    };

    const payloadRows = this.rows
      .map((row, i) => ({ row, i }))
      .filter(({ row }) => this.isMultiBrandSnapshotType(row.snapshotType))
      .map(({ row, i }) => {
        const level1Values = this.rowLevel1Values[i] ?? [];
        return {
          rowId: row.rowId ?? crypto.randomUUID(),
          configType: CONFIG_TYPE[this.mode],
          snapshotType: this.snapshotTypeOptions.find(o => o.value === row.snapshotType)?.label ?? row.snapshotType,
          sectionName: row.sectionName,
          subSectionName: row.subSectionName,
          brandList: this.isMultiBrandSnapshotType(row.snapshotType) ? (row.brandList || []) : [],
          dimension1: this.isBips ? null : row.dimension1,
          dimension2: this.isBips ? null : row.dimension2,
          dimension3: this.isBips ? null : row.dimension3,
          EntryPoint1: buildEntryPoint(i, row.entryPoint1, level1Values[0] ?? null),
          EntryPoint2: buildEntryPoint(i, row.entryPoint2, level1Values[1] ?? null),
          EntryPoint3: buildEntryPoint(i, row.entryPoint3, level1Values[2] ?? null),
          mandatory_variable_mapping: this.mandatoryVariableMapping ?? null,
          dimension: this.isBips ? null : (this.dimensionList ?? null)
        };
      });

    const uniquePayloads = new Set(
      payloadRows.map(r =>
        JSON.stringify({
          snapshotType: r.snapshotType,
          sectionName: r.sectionName,
          subSectionName: r.subSectionName,
          brandList: r.brandList,
          EntryPoint1: r.EntryPoint1,
          EntryPoint2: r.EntryPoint2,
          EntryPoint3: r.EntryPoint3
        })
      )
    );

    if (uniquePayloads.size !== payloadRows.length) {
      this._alertService.showAlert({ header: 'Error', message: 'Duplicate selection is not allowed', show: true });
      return null;
    }

    return payloadRows;
  }

  convertToSeparated(input: any) {
    const sharedFields = ["clientName", "studyName", "mandatory_variable_mapping", "dimension"];
    const result = [];
    const entryPointKeys = Object.keys(input).filter(k => k.startsWith("EntryPoint") && input[k] !== null);

    for (const key of entryPointKeys) {
      const obj = {
        [key]: input[key]
      };

      for (const field of sharedFields) {
        if (input[field] !== undefined) {
          obj[field] = input[field];
        }
      }
      result.push(obj);
    }

    return result;
  }
}
